import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import { format } from "date-fns";
import { ArrowLeft, Heart, Flag, LayoutGrid } from "lucide-react";
import api from "@/lib/api";
import { usePageMeta } from "@/lib/seo";
import { priceParts } from "@/lib/pricing";
import { mockListings } from "@/data/listings";
import { groupAmenities, labelFor, iconFor, isHtmlDescription } from "@/lib/amenities";
import { useAuth } from "@/context/AuthContext";
import NavTop from "@/components/nav/nav_top";
import ButtonMain from "@/components/buttons/button_main";
import CardListing from "@/components/cards/card_listing";
import BadgeVerified from "@/components/badges/badge_verified";
import { ListerBadge } from "@/components/badges/badge_realtor";
import PhotoLightbox from "@/components/media/photo_lightbox";
import ReportDialog from "@/components/listing/report_dialog";

const pin = (listing) =>
  L.divIcon({ className: "swiprent-pill-wrap", html: `<span class="swiprent-pill">$${priceParts(listing).amount.toLocaleString()}</span>`, iconSize: [0, 0] });

export default function ListingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  // REAL similar homes from the public feed (same city first) — never mocks.
  const [similar, setSimilar] = useState([]);
  // Mock listings are for explicit DEMO sessions ONLY. Guests and real users
  // who hit a missing listing get an honest error state, never a fake home.
  const canMock = !!user?.demo;

  // SEO: once the listing loads, upgrade the generic route title with the
  // real one (e.g. "Downtown Vancouver 2 Bed | SwipRent").
  usePageMeta(
    listing
      ? {
          title: listing.title,
          description: `${listing.beds === 0 ? "Studio" : `${listing.beds} bed`}, ${listing.baths} bath home for rent in ${listing.city || "Canada"} at $${priceParts(listing).amount.toLocaleString()} ${priceParts(listing).unit} on SwipRent.`,
        }
      : null
  );

  useEffect(() => {
    let alive = true;
    setNotFound(false);
    api
      .get(`/listings/${id}`)
      .then((res) => alive && setListing(res.data?.listing || res.data))
      .catch(() => {
        if (!alive) return;
        if (canMock) {
          setListing(mockListings.find((l) => String(l.id) === String(id)) || mockListings[0]);
        } else {
          setNotFound(true);
        }
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, canMock]);

  // Similar homes: REAL active listings from the same city (falling back to
  // the rest of the feed when the city has nothing else). No mock data —
  // when nothing real exists the strip simply does not render.
  useEffect(() => {
    if (!listing?.id) return undefined;
    let alive = true;
    const unwrap = (res) => {
      const data = Array.isArray(res.data) ? res.data : res.data?.listings || res.data?.items;
      return (Array.isArray(data) ? data : []).filter((l) => String(l.id) !== String(listing.id));
    };
    api
      .get("/listings", { params: { city: listing.city, limit: 7 } })
      .then((res) => {
        if (!alive) return null;
        const inCity = unwrap(res);
        if (inCity.length >= 1) {
          setSimilar(inCity.slice(0, 3));
          return null;
        }
        return api.get("/listings", { params: { limit: 7 } }).then((r2) => {
          if (alive) setSimilar(unwrap(r2).slice(0, 3));
        });
      })
      .catch(() => alive && setSimilar([]));
    return () => { alive = false; };
  }, [listing?.id, listing?.city]);

  // Escape collapses the expanded photo grid — but only while the lightbox is
  // closed, so the lightbox always gets first claim on the key.
  useEffect(() => {
    if (!showAll || lightbox !== null) return undefined;
    function onKey(e) {
      if (e.key === "Escape") setShowAll(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showAll, lightbox]);

  function like() {
    // WHY: guests cannot like anything server-side, so a success toast here
    // would be a lie. Send them to login and bring them straight back to
    // this listing afterwards (same pattern as report_dialog / PricingPage).
    if (!user) {
      navigate("/auth/login", { state: { from: `/listing/${id}` } });
      return;
    }
    api.post("/swipes", { target_id: id, target_type: "listing", action: "like" }).catch(() => {});
    toast.success("Liked. If the lister likes you back it is a match.");
    navigate("/swipe");
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-night">
        <NavTop />
        <main className="mx-auto flex max-w-md flex-col items-center px-6 pt-24 text-center">
          <h1 className="text-xl font-semibold text-night-text">We could not load this home</h1>
          <p className="mt-2 text-sm text-night-sub">
            It may have been removed, or something went wrong. Please try again.
          </p>
          <ButtonMain className="mt-6" onClick={() => navigate("/swipe")}>Back to swiping</ButtonMain>
        </main>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-night">
        <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-night-line border-t-brand-primary" />
      </div>
    );
  }

  const photos = listing.photos || [];
  const videoUrl = listing.video_url || null;
  const bedsLabel =
    (listing.beds === 0 ? "Studio" : `${listing.beds} bed`) +
    (listing.dens ? ` + ${listing.dens} den` : "");
  const availableLabel = listing.available_from ? format(new Date(listing.available_from), "MMM d") : "now";
  const factsLine = `${bedsLabel} · ${listing.baths} bath · ${listing.sqft} sqft · Available ${availableLabel}`;
  const extras = [
    listing.pets ? "Pets welcome" : "No pets",
    listing.furnished ? "Furnished" : "Unfurnished",
  ];
  // WHY: Leaflet throws on a missing/NaN center, which white-screens the
  // whole page. Null-check BEFORE coercing because Number(null) is 0, which
  // would silently drop the pin in the Atlantic instead of hiding the map.
  const hasCoords =
    listing.lat != null && listing.lng != null &&
    Number.isFinite(Number(listing.lat)) && Number.isFinite(Number(listing.lng));
  // Days on market from created_at (in the serializer payload). Null when
  // the timestamp is missing (older mocks) so the line simply does not show.
  const daysOnMarket = (() => {
    if (!listing.created_at) return null;
    const created = new Date(listing.created_at).getTime();
    if (!Number.isFinite(created)) return null;
    const days = Math.floor((Date.now() - created) / 86400000);
    return days >= 0 ? days : null;
  })();
  const onMarketLine =
    daysOnMarket === null
      ? null
      : daysOnMarket === 0
      ? "On SwipRent since today"
      : `On SwipRent for ${daysOnMarket} ${daysOnMarket === 1 ? "day" : "days"}`;

  return (
    <div className="min-h-screen bg-night pb-[calc(8.5rem+env(safe-area-inset-bottom))] md:pb-28 lg:pb-10">
      <NavTop />
      <main className="mx-auto max-w-6xl px-4 pt-6 sm:px-6">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-night-sub transition hover:text-night-text"
        >
          <ArrowLeft size={15} /> Back
        </button>

        {/* Title row */}
        <h1 className="text-[26px] font-semibold tracking-tight text-night-text">{listing.title}</h1>
        <div className="mt-1 flex flex-wrap items-center gap-3">
          <p className="text-sm text-night-sub">
            {/* address already includes the city/province/postal, so show it
                alone (no duplicate ", Vancouver"); fall back to city, province. */}
            {listing.address || [listing.city, listing.province].filter(Boolean).join(", ")}
          </p>
          <span className="flex gap-1.5">
            {listing.verified && <BadgeVerified className="border border-night-line shadow-none" />}
            <ListerBadge type={listing.lister_type} className="border border-night-line shadow-none" />
          </span>
        </div>

        {/* Gallery: video tour first when there is one, then photos */}
        <div className="relative mt-5 grid h-72 grid-cols-4 grid-rows-2 gap-2 overflow-hidden rounded-2xl md:h-[420px]">
          <div className="col-span-4 row-span-2 md:col-span-2">
            {videoUrl ? (
              /* No poster (older uploads): the #t=0.1 fragment makes the
                 browser paint the first frame instead of a black box. */
              <video
                src={listing.video_poster ? videoUrl : `${videoUrl}#t=0.1`}
                poster={listing.video_poster || undefined}
                controls
                playsInline
                preload="metadata"
                className="h-full w-full rounded-2xl bg-black object-cover md:rounded-none"
              />
            ) : (
              <img
                src={photos[0]}
                alt={listing.title}
                decoding="async"
                onClick={() => setLightbox(0)}
                className="h-full w-full cursor-zoom-in object-cover"
              />
            )}
          </div>
          {[1, 2, 3, 4].map((i) => {
            // With a video in the hero slot, photos start from index 0 here
            const photoIndex = photos.length
              ? (videoUrl ? i - 1 : i) % photos.length
              : null;
            return (
              <div key={i} className="hidden md:block">
                {photoIndex !== null && (
                  <img
                    src={photos[photoIndex]}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    onClick={() => setLightbox(photoIndex)}
                    className="h-full w-full cursor-zoom-in object-cover"
                  />
                )}
              </div>
            );
          })}
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="absolute bottom-4 right-4 inline-flex items-center gap-2 rounded-lg border border-night-line bg-night-surface px-3.5 py-1.5 text-sm font-medium text-night-text transition hover:bg-night-tint"
          >
            <LayoutGrid size={14} /> {showAll ? "Hide photos" : "Show all photos"}
          </button>
        </div>
        {showAll && (
          <div className="mt-2 grid grid-cols-2 gap-2 md:grid-cols-3">
            {videoUrl && (
              <video
                src={listing.video_poster ? videoUrl : `${videoUrl}#t=0.1`}
                poster={listing.video_poster || undefined}
                controls
                playsInline
                preload="metadata"
                className="aspect-[4/3] w-full rounded-2xl bg-black object-cover"
              />
            )}
            {photos.map((p, i) => (
              <img
                key={i}
                src={p}
                alt=""
                loading="lazy"
                decoding="async"
                onClick={() => setLightbox(i)}
                className="aspect-[4/3] w-full cursor-zoom-in rounded-2xl object-cover"
              />
            ))}
          </div>
        )}

        <div className="mt-8 grid gap-12 lg:grid-cols-[1fr_360px]">
          <div>
            {/* Facts line */}
            <p className="text-base text-night-text">{factsLine}</p>
            <p className="mt-1 text-sm text-night-sub">{extras.join(" · ")}</p>
            {onMarketLine && <p className="mt-1 text-xs text-night-sub">{onMarketLine}</p>}

            {/* Host — tapping it opens all of this lister's listings. */}
            <Link
              to={listing.owner_id ? `/hosts/${listing.owner_id}` : "#"}
              className="mt-6 flex items-center gap-4 border-t border-night-line pt-6 transition hover:opacity-90"
            >
              {listing.lister_photo ? (
                <img
                  src={listing.lister_photo}
                  alt={listing.lister_name || "Lister"}
                  loading="lazy"
                  decoding="async"
                  className="h-12 w-12 shrink-0 rounded-full bg-night-tint object-cover ring-1 ring-night-line"
                />
              ) : (
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand-primary text-lg font-semibold text-white">
                  {listing.lister_name?.slice(0, 1)}
                </span>
              )}
              <div>
                <p className="flex items-center gap-2 font-medium text-night-text">
                  Listed by {listing.lister_name?.split(",")[0]}
                  {(listing.lister_verified || listing.lister_kyc_verified || listing.kyc_verified) && (
                    <BadgeVerified label="ID verified" className="border border-night-line shadow-none" />
                  )}
                </p>
                <p className="text-sm text-night-sub">
                  {listing.lister_type === "realtor" ? "Licensed realtor" : "Owner"}
                  {listing.verified && " · Verified"}
                </p>
                {(listing.lister_brokerage || listing.courtesy_of) && (
                  <p className="text-sm text-night-sub">
                    Courtesy of {listing.lister_brokerage || listing.courtesy_of}
                  </p>
                )}
                <p className="mt-1 text-sm font-semibold text-brand-lime">See all their listings →</p>
              </div>
            </Link>

            {/* About: the wizard stores sanitized HTML (bold/lists), older
                listings store plain text — render each correctly. */}
            {listing.description && (
              <div className="mt-6 border-t border-night-line pt-6">
                <h2 className="text-lg font-semibold tracking-tight text-night-text">About this home</h2>
                {isHtmlDescription(listing.description) ? (
                  <div
                    className="prose-listing mt-2 leading-relaxed text-night-text"
                    // Sanitized server-side (services/html_sanitize.py) before storage.
                    dangerouslySetInnerHTML={{ __html: listing.description }}
                  />
                ) : (
                  <p className="mt-2 whitespace-pre-line leading-relaxed text-night-text">{listing.description}</p>
                )}
              </div>
            )}

            {/* Amenities — grouped Airbnb-style with icons and proper labels,
                never raw snake_case keys. */}
            {groupAmenities(listing).length > 0 && (
              <div className="mt-6 border-t border-night-line pt-6">
                <h2 className="text-lg font-semibold tracking-tight text-night-text">What this place offers</h2>
                <div className="mt-4 space-y-6">
                  {groupAmenities(listing).map((group) => (
                    <div key={group.title}>
                      <p className="text-xs font-semibold uppercase tracking-wide text-night-sub">{group.title}</p>
                      <div className="mt-2.5 grid grid-cols-1 gap-y-3 sm:grid-cols-2">
                        {group.items.map((a) => {
                          const Icon = iconFor(a);
                          return (
                            <span key={a} className="inline-flex items-center gap-3 text-[15px] text-night-text">
                              <Icon size={18} strokeWidth={1.8} className="shrink-0 text-night-sub" />
                              {labelFor(a)}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Location: listings without coordinates simply skip the map. */}
            {hasCoords && (
              <div className="mt-6 border-t border-night-line pt-6">
                <h2 className="text-lg font-semibold tracking-tight text-night-text">Where you'll live</h2>
                <div className="isolate mt-3 h-64 overflow-hidden rounded-2xl border border-night-line">
                  <MapContainer center={[Number(listing.lat), Number(listing.lng)]} zoom={14} className="h-full w-full" scrollWheelZoom={false}>
                    <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <Marker position={[Number(listing.lat), Number(listing.lng)]} icon={pin(listing)} />
                  </MapContainer>
                </div>
              </div>
            )}
          </div>

          {/* Sticky price panel */}
          <aside className="hidden lg:block">
            <div className="sticky top-28 rounded-2xl border border-night-line bg-night-surface p-6 shadow-ambient">
              <p className="text-[22px] text-night-text">
                <span className="font-semibold text-brand-primary">${priceParts(listing).amount.toLocaleString()}</span>{" "}
                <span className="text-base text-night-sub">{priceParts(listing).unit}</span>
              </p>
              <ButtonMain full className="mt-4 py-3" onClick={like}>
                <Heart size={16} /> Like this place
              </ButtonMain>
              <p className="mt-3 text-center text-xs text-night-sub">
                Liking is free. Chat opens when the lister likes you back.
              </p>
              <button
                type="button"
                onClick={() => setReportOpen(true)}
                className="mt-4 inline-flex w-full items-center justify-center gap-1.5 text-xs text-night-sub transition hover:text-night-text hover:underline"
              >
                <Flag size={12} /> Report this listing
              </button>
            </div>
          </aside>
        </div>

        {/* Mobile report access (the desktop aside is hidden below lg) */}
        <div className="mt-8 border-t border-night-line pt-5 lg:hidden">
          <button
            type="button"
            onClick={() => setReportOpen(true)}
            className="inline-flex items-center gap-2 text-sm font-medium text-night-sub transition hover:text-night-text"
          >
            <Flag size={14} /> Report this listing
          </button>
        </div>

        {similar.length > 0 && (
          <div className="mt-12 border-t border-night-line pt-10">
            <h2 className="text-lg font-semibold tracking-tight text-night-text">Similar homes in {listing.city}</h2>
            <div className="mt-5 grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
              {similar.map((l) => (
                <CardListing key={l.id} listing={l} onClick={() => navigate(`/listing/${l.id}`)} />
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Mobile action bar */}
      <div className="fixed inset-x-0 bottom-[calc(3.75rem+env(safe-area-inset-bottom))] z-30 flex items-center justify-between gap-4 border-t border-night-line bg-night-surface px-4 py-3 md:bottom-0 md:pb-[calc(0.75rem+env(safe-area-inset-bottom))] lg:hidden">
        <p className="text-night-text">
          <span className="font-semibold text-brand-primary">${priceParts(listing).amount.toLocaleString()}</span>{" "}
          <span className="text-sm text-night-sub">{priceParts(listing).unit}</span>
        </p>
        <ButtonMain onClick={like}><Heart size={16} /> Like this place</ButtonMain>
      </div>

      {lightbox !== null && photos.length > 0 && (
        <PhotoLightbox photos={photos} index={lightbox} onClose={() => setLightbox(null)} onIndex={setLightbox} />
      )}

      <ReportDialog
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        listingId={listing.id}
        listingTitle={listing.title}
      />
    </div>
  );
}
